import { createFunction as createRemoteFunction } from '../../firebase/firebase';
import {
  ICreatePartyRequest,
  IJoinAreaRequest,
  IConfigureAreaRequest,
  IJoinPartyRequest,
  IRequest,
  IHighlightParticipantRequest,
  IToggleParticipantRequest,
  IInviteParticipantToAreaRequest,
  ISendMessageRequest,
  IRunDebugFunctionRequest,
} from './PartyClientRequests';
import {
  IJoinPartyResponse,
  IMessage,
  IPartyForUser,
  TParticipantInviteForUserMap,
} from '../../PartyClientResponse';
import { IRespondToParticipantInviteRequest } from '../../PartyClientRequests';
import { LOG_EVENT } from '../../services/LoggingService';

export const getVersion = async (): Promise<string> => {
  const getVersionFunction = createRemoteFunction<void, { version: string }>('version');
  const { version } = await getVersionFunction();
  return version;
};

export const runDebugFunction = async (params: IRunDebugFunctionRequest): Promise<void> => {
  const runDebugFunctionFunction = createRemoteFunction<IRequest, IPartyForUser[]>(
    'runDebugFunction'
  );
  await runDebugFunctionFunction(params);
  // return parties;
};

export const getAllPartiesForUser = async (params: IRequest): Promise<IPartyForUser[]> => {
  const getAllPartiesForUserFunction = createRemoteFunction<IRequest, IPartyForUser[]>(
    'getAllPartiesForUser'
  );
  const parties = await getAllPartiesForUserFunction(params);
  return parties;
};

export const createParty = async (params: ICreatePartyRequest): Promise<IPartyForUser> => {
  LOG_EVENT('create_party', { party_name: params.name });
  const createPartyFunction = createRemoteFunction<ICreatePartyRequest, IPartyForUser>(
    'createParty'
  );
  return await createPartyFunction(params);
};

export const joinParty = async (params: IJoinPartyRequest): Promise<IJoinPartyResponse> => {
  LOG_EVENT('join_party', { party_key: params.key });
  const joinPartyFunction = createRemoteFunction<IJoinPartyRequest, IJoinPartyResponse>(
    'joinParty'
  );
  return await joinPartyFunction(params);
};

export const pingParty = async (params: IJoinPartyRequest): Promise<true | null> => {
  const pingPartyFunction = createRemoteFunction<IJoinPartyRequest, true>('pingParty');
  return await pingPartyFunction(params);
};

export const configureArea = async (params: IConfigureAreaRequest): Promise<IPartyForUser> => {
  LOG_EVENT('configure_area', {
    party_key: params.key,
    area_key: params.area,
    settings: params.settings,
  });
  const configureAreaFunction = createRemoteFunction<IConfigureAreaRequest, IPartyForUser>(
    'configureArea'
  );
  const party = await configureAreaFunction(params);
  return party;
};

export const joinArea = async (params: IJoinAreaRequest): Promise<IPartyForUser> => {
  LOG_EVENT('join_area', { party_key: params.key, area_key: params.area });
  const joinAreaFunction = createRemoteFunction<IJoinAreaRequest, IPartyForUser>('joinArea');
  const party = await joinAreaFunction(params);
  return party;
};

export const respondToParticpantInviteRequest = async (
  params: IRespondToParticipantInviteRequest
): Promise<IPartyForUser> => {
  const respondToParticipantInviteFunction = createRemoteFunction<
    IRespondToParticipantInviteRequest,
    IPartyForUser
  >('respondToParticipantInvite');
  LOG_EVENT('respond_to_invite_request', {
    party_key: params.invite.partyKey,
    inviter_id: params.invite.inviterId,
    invitee_id: params.invite.inviteeId,
  });
  const party = await respondToParticipantInviteFunction(params);
  return party;
};

export const highlightParticipant = async (
  params: IHighlightParticipantRequest
): Promise<IPartyForUser> => {
  const highlightParticipantFunction = createRemoteFunction<
    IHighlightParticipantRequest,
    IPartyForUser
  >('highlightParticipant');
  const party = await highlightParticipantFunction(params);
  return party;
};

export const toggleParticipantTag = async (
  params: IToggleParticipantRequest
): Promise<IPartyForUser> => {
  const toggleParticipantTagFunction = createRemoteFunction<
    IToggleParticipantRequest,
    IPartyForUser
  >('toggleParticipantTag');
  const party = await toggleParticipantTagFunction(params);
  return party;
};

export const clientInviteParticipantToArea = async (
  params: IInviteParticipantToAreaRequest
): Promise<void> => {
  LOG_EVENT('invite_participant_to_room', {
    invitee_id: params.inviteeId,
    area_key: params.area,
    party_key: params.key,
  });
  const inviteParticipantToAreaFunction = createRemoteFunction<
    IInviteParticipantToAreaRequest,
    TParticipantInviteForUserMap
  >('inviteParticipantToArea');
  await inviteParticipantToAreaFunction(params);
};

export const sendMessage = async (
  params: ISendMessageRequest
): Promise<{ [key in string]: IMessage }> => {
  LOG_EVENT('send_message', {
    target_id: params.targetId,
    party_key: params.partyKey,
  });
  const sendMessageFunction = createRemoteFunction<ISendMessageRequest>('sendMessage');
  return await sendMessageFunction(params);
};
